import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  doc,
  increment,
  where,
  getDoc,
} from "firebase/firestore";
import { db } from "../config";
import NavBar from "./NavBar";
import axios from "axios";

function Deposits() {
  const [deposits, setDeposits] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);

  const fetchDeposits = async (next = false) => {
    setLoading(true);
    try {
      const depositRef = collection(db, "deposits");
      let q = query(depositRef, orderBy("timestamp", "desc"), limit(10));

      if (next && lastVisible) {
        q = query(
          depositRef,
          orderBy("timestamp", "desc"),
          startAfter(lastVisible),
          limit(10)
        );
      }

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      setDeposits((prev) => (next ? [...prev, ...data] : data));
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
    } catch (error) {
      console.error("Error fetching deposits:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeposits();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    if (typeof timestamp === "string") {
      const parsed = new Date(timestamp);
      return isNaN(parsed) ? "N/A" : parsed.toLocaleString();
    }
    return "N/A";
  };

  const handleStatusChange = async (depositId, newStatus, deposit) => {
    // Prevent re-processing already successful deposits
    if (deposit.status === "success" || deposit.status === newStatus) {
      return;
    }

    setStatusUpdating(depositId);

    try {
      const depositRef = doc(db, "deposits", depositId);
      await updateDoc(depositRef, { status: newStatus });

      if (newStatus === "success") {
        // 1. Increase Totaldeposit (but NOT balance)
        const userRef = doc(db, "users", deposit.userId);
        await updateDoc(userRef, {
          Totaldeposit: increment(deposit.amount),
        });

        // 2. Activate the linked investment — but only if still pending
        if (deposit.investmentId) {
          const investmentRef = doc(db, "investments", deposit.investmentId);
          const investmentSnap = await getDoc(investmentRef);

          if (investmentSnap.exists()) {
            const invData = investmentSnap.data();
            if (invData.status === "pending") {
              await updateDoc(investmentRef, {
                status: "active",
                activatedAt: new Date(),
              });
              console.log(`Investment ${deposit.investmentId} activated`);
            } else {
              console.log(
                `Investment ${deposit.investmentId} was already ${invData.status} — skipping activation`
              );
            }
          } else {
            console.warn(`Investment ${deposit.investmentId} not found`);
          }
        }

        // 3. Send updated success email (investment-focused)
        await axios.post("https://renderbackend-24k2.onrender.com/send-mail", {
          email: deposit.userEmail,
          subject: "Your Deposit & Investment Confirmed",
          CompanyName: "VYNTACAPITALS",
          htmlToSend: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
              <div style="text-align: center; margin-bottom: 20px;">
                <img src="https://vyntacapitals.com/public/uploads/logo/light.png" alt="VYNTACAPITALS Logo" style="height: 60px;" />
                <h2 style="margin: 10px 0; color: #1a1a1a;">VYNTACAPITALS</h2>
              </div>
              <h3 style="color: #333;">Great News, INVESTOR ${deposit.username || "Investor"}!</h3>
              <p style="color: #555;">
                Your deposit of <strong>$${parseFloat(deposit.amount).toFixed(2)}</strong> 
                via <strong>${deposit.method || "selected method"}</strong> has been successfully confirmed.
              </p>
              <p style="color: #555;">
                Your investment in the <strong>"${deposit.plan || "selected plan"}"</strong> plan 
                is now <strong>active</strong> and earning returns.
              </p>
              <p style="color: #555; margin-top: 20px;">
                You can track your investment progress in your dashboard.
              </p>
              <p style="margin-top: 25px; color: #555;">
                Thank you for trusting VYNTACAPITALS with your investment!
              </p>
              <p style="color: #888;">– The VYNTACAPITALS Team</p>
              <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
              <p style="font-size: 12px; color: #aaa; text-align: center;">
                © ${new Date().getFullYear()} VYNTACAPITALS. All rights reserved.
              </p>
            </div>
          `,
        });

        // 4. Referral bonus (unchanged logic)
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const referredUserId = userData.referreduserid;

          if (referredUserId) {
            const usersRef = collection(db, "users");
            const referralQuery = query(
              usersRef,
              where("myReferralId", "==", referredUserId)
            );
            const referralSnapshot = await getDocs(referralQuery);

            if (!referralSnapshot.empty) {
              const referrerDoc = referralSnapshot.docs[0];
              const referrerId = referrerDoc.id;
              const bonusAmount = 0.05 * deposit.amount;

              const referrerRef = doc(db, "users", referrerId);
              await updateDoc(referrerRef, {
                balance: increment(bonusAmount),
              });

              // Referral email (unchanged)
              await axios.post("https://renderbackend-24k2.onrender.com/send-mail", {
                email: referrerDoc.data().email,
                subject: "Referral Bonus Earned",
                CompanyName: "VYNTACAPITALS",
                htmlToSend: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
                    <div style="text-align: center; margin-bottom: 20px;">
                      <img src="https://vyntacapitals.com/public/uploads/logo/light.png" alt="VYNTACAPITALS Logo" style="height: 60px;" />
                      <h2 style="margin: 10px 0; color: #1a1a1a;">VYNTACAPITALS</h2>
                    </div>
                    <h3 style="color: #333;">Referral Bonus Earned!</h3>
                    <p style="color: #555;">
                      Congratulations! You've earned a referral bonus from a successful deposit:
                    </p>
                    <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
                      <tr>
                        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Referred User:</strong></td>
                        <td style="padding: 8px; border-bottom: 1px solid #ddd;">${deposit.userEmail || "N/A"}</td>
                      </tr>
                      <tr>
                        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Deposit Amount:</strong></td>
                        <td style="padding: 8px; border-bottom: 1px solid #ddd;">$${parseFloat(deposit.amount).toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Bonus Amount:</strong></td>
                        <td style="padding: 8px; border-bottom: 1px solid #ddd;">$${parseFloat(bonusAmount).toFixed(2)}</td>
                      </tr>
                    </table>
                    <p style="color: #555;">Your account has been credited with the bonus.</p>
                    <p style="margin-top: 20px; color: #555;">Thank you for growing the VYNTACAPITALS community!</p>
                    <p style="color: #888;">– The VYNTACAPITALS Team</p>
                    <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
                    <p style="font-size: 12px; color: #aaa; text-align: center;">
                      © ${new Date().getFullYear()} VYNTACAPITALS. All rights reserved.
                    </p>
                  </div>
                `,
              });
            }
          }
        }
      }

      // Update UI
      setDeposits((prev) =>
        prev.map((item) =>
          item.id === depositId ? { ...item, status: newStatus } : item
        )
      );
    } catch (error) {
      console.error("Error updating deposit/investment/email:", error);
      alert("Failed to process status change. Check console for details.");
    } finally {
      setStatusUpdating(null);
    }
  };

  return (
    <div className="container-fluid px-3 py-4">
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a className="nav-link" data-widget="pushmenu" href="#" role="button">
              <i className="fas fa-bars" />
            </a>
          </li>
        </ul>
      </nav>

      <NavBar />

      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <h3 className="text-center mb-4">Manage Deposits</h3>

          {loading && (
            <div className="text-center mb-3">
              <i className="fas fa-spinner fa-spin fa-2x"></i> Loading...
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Email</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Plan</th>
                  <th>Timestamp</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => (
                  <tr key={deposit.id}>
                    <td>{deposit.userEmail || "—"}</td>
                    <td>{deposit.method || "—"}</td>
                    <td>
                      <strong style={{ color: "#0d6efd" }}>
                        ${parseFloat(deposit.amount || 0).toFixed(2)}
                      </strong>
                    </td>
                    <td>{deposit.plan || "—"}</td>
                    <td>{formatDate(deposit.timestamp)}</td>
                    <td>
                      <span
                        className={`badge bg-${
                          deposit.status === "success"
                            ? "success"
                            : deposit.status === "failed"
                            ? "danger"
                            : "warning"
                        }`}
                      >
                        {deposit.status?.toUpperCase() || "PENDING"}
                      </span>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={deposit.status || "pending"}
                        disabled={statusUpdating === deposit.id || deposit.status === "success"}
                        onChange={(e) =>
                          handleStatusChange(deposit.id, e.target.value, deposit)
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="success">Confirm (Activate)</option>
                        <option value="failed">Reject</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {lastVisible && (
            <div className="text-center mt-4">
              <button
                className="btn btn-outline-primary px-4"
                onClick={() => fetchDeposits(true)}
                disabled={loading}
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}

          {!loading && deposits.length === 0 && (
            <p className="text-center text-muted mt-4">No deposits found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Deposits;
// import React, { useEffect, useState } from "react";
// import {
//   collection,
//   getDocs,
//   query,
//   orderBy,
//   limit,
//   startAfter,
//   updateDoc,
//   doc,
//   increment,
//   where,
//   getDoc,
// } from "firebase/firestore";
// import { db } from "../config";
// import NavBar from "./NavBar";
// import axios from "axios";

// function Deposits() {
//   const [deposits, setDeposits] = useState([]);
//   const [lastVisible, setLastVisible] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [statusUpdating, setStatusUpdating] = useState(null);

//   const fetchDeposits = async (next = false) => {
//     setLoading(true);
//     try {
//       const depositRef = collection(db, "deposits");
//       let q = query(depositRef, orderBy("timestamp", "desc"), limit(10));

//       if (next && lastVisible) {
//         q = query(
//           depositRef,
//           orderBy("timestamp", "desc"),
//           startAfter(lastVisible),
//           limit(10)
//         );
//       }

//       const snapshot = await getDocs(q);
//       const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

//       setDeposits((prev) => (next ? [...prev, ...data] : data));
//       setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
//     } catch (error) {
//       console.error("Error fetching deposits:", error);
//     }
//     setLoading(false);
//   };

//   useEffect(() => {
//     fetchDeposits();
//   }, []);

//   // Helper function to format timestamp
//   const formatDate = (timestamp) => {
//     if (!timestamp) return "N/A";
//     if (timestamp.seconds) {
//       // Firestore Timestamp
//       return new Date(timestamp.seconds * 1000).toLocaleString();
//     }
//     if (timestamp instanceof Date) {
//       // JavaScript Date
//       return timestamp.toLocaleString();
//     }
//     if (typeof timestamp === "string") {
//       // ISO string or other string
//       const parsedDate = new Date(timestamp);
//       return isNaN(parsedDate) ? "N/A" : parsedDate.toLocaleString();
//     }
//     return "N/A";
//   };

// const handleStatusChange = async (id, newStatus, deposit) => {
//   // Prevent status change if already 'success'
//   if (deposit.status === "success") return;

//   setStatusUpdating(id);
//   try {
//     const depositRef = doc(db, "deposits", id);
//     await updateDoc(depositRef, { status: newStatus });

//     // If status is success, update user's total deposit and balance + handle referral
//     if (newStatus === "success") {
//       const userRef = doc(db, "users", deposit.userId);
//       await updateDoc(userRef, {
//         Totaldeposit: increment(deposit.amount),
//         balance: increment(deposit.amount), // Add deposit amount to user's wallet balance
//       });
//       console.log(`✅ Added $${deposit.amount} to user ${deposit.userId} balance and Totaldeposit`);

//       // Send deposit success email (updated wording)
//       await axios.post("https://renderbackend-24k2.onrender.com/send-mail", {
//         email: deposit.userEmail,
//         subject: "Deposit Successfully Processed",
//         CompanyName: "VYNTACAPITALS",
//         htmlToSend: `
//           <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
//             <div style="text-align: center; margin-bottom: 20px;">
//               <img src="https://vyntacapitals.com/public/uploads/logo/light.png" alt="VYNTACAPITALS Logo" style="height: 60px;" />
//               <h2 style="margin: 10px 0; color: #1a1a1a;">VYNTACAPITALS</h2>
//             </div>
//             <h3 style="color: #333;">Congratulations! INVESTOR ${deposit.username}</h3>
//             <p style="color: #555;">Your deposit of $${parseFloat(deposit.amount).toFixed(2)} has been successfully processed and added to your wallet balance.</p>
//             <p style="color: #555;">You can now use these funds for investments or withdrawals as needed.</p>
//             <p style="margin-top: 20px; color: #555;">Thank you for choosing VYNTACAPITALS!</p>
//             <p style="color: #888;">– The VYNTACAPITALS Team</p>
//             <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
//             <p style="font-size: 12px; color: #aaa; text-align: center;">
//               © ${new Date().getFullYear()} VYNTACAPITALS. All rights reserved.
//             </p>
//           </div>
//         `,
//       });

//       // Referral system remains exactly as it was
//       const userRef2 = doc(db, "users", deposit.userId);
//       const userSnap = await getDoc(userRef2);

//       if (!userSnap.exists()) {
//         console.error("User not found");
//         return;
//       }

//       const userData = userSnap.data();
//       const referredUserId = userData.referreduserid;

//       const usersRef = collection(db, "users");
//       const referralQuery = query(
//         usersRef,
//         where("myReferralId", "==", referredUserId)
//       );
//       const referralSnapshot = await getDocs(referralQuery);

//       if (!referralSnapshot.empty) {
//         const referrer = referralSnapshot.docs[0].data();
//         const referrerId = referralSnapshot.docs[0].id;
//         const bonusAmount = 0.05 * deposit.amount;

//         // Increment referrer's balance
//         const referrerRef = doc(db, "users", referrerId);
//         await updateDoc(referrerRef, {
//           balance: increment(bonusAmount),
//         });
//         console.log(
//           `✅ Awarded referral bonus of $${bonusAmount.toFixed(2)} to user: ${referrerId}`
//         );

//         // Send referral bonus email (unchanged)
//         await axios.post("https://renderbackend-24k2.onrender.com/send-mail", {
//           email: referrer.email,
//           subject: "Referral Bonus Earned",
//           CompanyName: "VYNTACAPITALS",
//           htmlToSend: `
//             <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
//               <div style="text-align: center; margin-bottom: 20px;">
//                 <img src="https://vyntacapitals.com/public/uploads/logo/light.png" alt="VYNTACAPITALS Logo" style="height: 60px;" />
//                 <h2 style="margin: 10px 0; color: #1a1a1a;">VYNTACAPITALS</h2>
//               </div>
//               <h3 style="color: #333;">Referral Bonus Earned!</h3>
//               <p style="color: #555;">Congratulations! You've earned a referral bonus due to a successful deposit by a user you referred:</p>
//               <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
//                 <tr>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Referred User:</strong></td>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;">${deposit.userEmail || "N/A"}</td>
//                 </tr>
//                 <tr>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Deposit Amount:</strong></td>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style={{ color: "#212529" }}>${parseFloat(deposit.amount).toFixed(2)}</span></td>
//                 </tr>
//                 <tr>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Bonus Amount:</strong></td>
//                   <td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style={{ color: "#212529" }}>${parseFloat(bonusAmount).toFixed(2)}</span></td>
//                 </tr>
//               </table>
//               <p style="color: #555;">Your account has been credited with a referral bonus of <span style={{ color: "#212529" }}>${parseFloat(bonusAmount).toFixed(2)}</span>.</p>
//               <p style="margin-top: 20px; color: #555;">Thank you for referring users to VYNTACAPITALS!</p>
//               <p style="color: #888;">– The VYNTACAPITALS Team</p>
//               <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
//               <p style="font-size: 12px; color: #aaa; text-align: center;">
//                 © ${new Date().getFullYear()} VYNTACAPITALS. All rights reserved.
//               </p>
//             </div>
//           `,
//         });
//       }
//     }

//     // Update local state
//     setDeposits((prev) =>
//       prev.map((item) =>
//         item.id === id ? { ...item, status: newStatus } : item
//       )
//     );
//   } catch (error) {
//     console.error("Failed to update status, balance, or send email:", error);
//   }
//   setStatusUpdating(null);
// };

//   return (
//     <div className="container-fluid px-3 py-4">
//       <nav className="main-header navbar navbar-expand navbar-white navbar-light">
//         <ul className="navbar-nav">
//           <li className="nav-item">
//             <a
//               className="nav-link"
//               data-widget="pushmenu"
//               href="#"
//               role="button"
//             >
//               <i className="fas fa-bars" />
//             </a>
//           </li>
//         </ul>
//       </nav>

//       <NavBar />

//       <div className="row justify-content-center">
//         <div className="col-12 col-lg-10">
//           <h3 className="text-center mb-4">Deposits</h3>
//           {loading && (
//             <div className="text-center mb-3">
//               <i className="fas fa-spinner fa-spin fa-2x"></i> Loading...
//             </div>
//           )}

//           <div className="table-responsive">
//             <table className="table table-bordered table-hover">
//               <thead className="table-dark">
//                 <tr>
//                   <th>Email</th>
//                   <th>Method</th>
//                   <th>Amount</th>
//                   <th>Timestamp</th>
//                   <th>Status</th>
//                   <th>Change Status</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {deposits.map((deposit) => (
//                   <tr key={deposit.id}>
//                     <td >{deposit.userEmail || "N/A"}</td>
//                     <td>{deposit.method || "N/A"}</td>
//                     <td><span style={{ color: "#212529" }}>${parseFloat(deposit.amount || 0).toFixed(2)}</span></td>
//                     <td>{formatDate(deposit.timestamp)}</td>
//                     <td>
//                       <span
//                         className={`badge bg-${
//                           deposit.status === "success"
//                             ? "success"
//                             : deposit.status === "failed"
//                             ? "danger"
//                             : "warning"
//                         }`}
//                       >
//                         {deposit.status || "N/A"}
//                       </span>
//                     </td>
//                     <td>
//                       <select
//                         className="form-select form-select-sm"
//                         disabled={statusUpdating === deposit.id || deposit.status === "success"}
//                         value={deposit.status || "pending"}
//                         onChange={(e) =>
//                           handleStatusChange(
//                             deposit.id,
//                             e.target.value,
//                             deposit
//                           )
//                         }
//                         aria-label={`Change status for deposit ${deposit.id}`}
//                       >
//                         <option value="pending">Pending</option>
//                         <option value="success">Success</option>
//                         <option value="failed">Failed</option>
//                       </select>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {lastVisible && (
//             <div className="text-center mt-3">
//               <button
//                 className="btn btn-outline-primary"
//                 onClick={() => fetchDeposits(true)}
//                 disabled={loading}
//               >
//                 Load More
//               </button>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Deposits;
